/**
 * School admin → Staff → Import a CSV.
 *
 * The staff list's answer to the student import: a file (or a paste from a
 * spreadsheet) read in the browser through the same tokenizer, validated row by
 * row against the same contract the server uses, and sent only once every row
 * reads. The server then adds each PERSON — rows sharing an email are one
 * teacher taking several classes — through the same `createStaff` as the
 * single form.
 *
 * What differs from students is the password. A school chooses a student's;
 * a staff member's is generated, so the result is the one place each new
 * temporary password is shown. It is shown once, and can be saved as a CSV to
 * hand out — there is no way back to it afterwards, exactly as with the single
 * form's reveal.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MEMBERSHIP_ROLE_LABEL } from '@cwy/config/contracts';
import type { ClassroomDTO, StaffImportResultDTO } from '@cwy/config/contracts';
import { apiPost } from '@/lib/api';
import { fontPx } from '@/lib/fontSize';
import { downloadCsv, parseStaffRoster, STAFF_COLUMN_GUIDE, staffSample } from '@/lib/rosterImport';
import { Button, Card, Field } from '@/components/ds';
import { ImportFormatGuide } from '@/components/admin/ImportFormatGuide';
import { useOfferedSubjects } from '@/components/admin/ClassroomsManager';

export function StaffImportCard({
  rooms,
  offered,
  onImported,
}: {
  rooms: ClassroomDTO[];
  /** The school's curriculum selection, so the sample names subjects it runs. */
  offered: ReadonlySet<string>;
  onImported: (result: StaffImportResultDTO) => void;
}) {
  // The first class's subjects — the ones a sample row and the hint can name
  // knowing the server will find them there.
  const firstRoom = rooms[0] ?? null;
  const { subjects } = useOfferedSubjects(
    firstRoom?.board ?? null,
    firstRoom?.grade ?? null,
    offered,
    firstRoom?.academicYear?.id ?? null,
  );
  const subjectNames = subjects.map((subject) => subject.name);
  const [paste, setPaste] = useState('');
  const [error, setError] = useState('');
  const parsed = parseStaffRoster(paste);
  const people = new Set(parsed.staff.map((row) => row.email.toLowerCase())).size;

  const importStaff = useMutation({
    mutationFn: () => {
      if (parsed.errors.length) throw new Error('Fix the invalid rows before importing.');
      return apiPost<StaffImportResultDTO>('/tenant-admin/staff/import', { staff: parsed.staff });
    },
    onSuccess: (result) => {
      setPaste('');
      onImported(result);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card title="Import a list of staff">
      <p className="mb-4 text-sm text-ink-soft">
        One row per teacher and class. Only a name and an email are needed; the other columns give a
        teacher their class, subjects and class-teacher role in the same step. A teacher who takes
        more than one class goes on one row per class, with the same email.
      </p>
      <div className="flex flex-col gap-4">
        <ImportFormatGuide
          columns={STAFF_COLUMN_GUIDE}
          sampleFileName="staff-import-sample.csv"
          sample={() =>
            staffSample(
              rooms.map((room) => room.code),
              subjectNames,
            )
          }
          rowNoun="teacher and class"
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="cursor-pointer">
            <span className="inline-flex rounded-[var(--control-radius)] border border-line px-3 py-1.5 text-sm font-semibold text-ink">
              Choose a CSV file
            </span>
            <input
              type="file"
              accept=".csv,text/csv,.txt,text/plain"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                setError('');
                file
                  .text()
                  .then(setPaste)
                  .catch(() => setError('Could not read that file.'));
              }}
            />
          </label>
          <span className="text-xs text-ink-soft">
            The header row is optional. Nothing is sent until you press Import.
          </span>
        </div>

        <Field
          label="Or paste one row per line"
          hint="Role is Teacher or Admin (Teacher if left empty). Separate several subjects with semicolons. Class teacher is Yes or No."
        >
          {({ id }) => (
            <textarea
              id={id}
              data-control=""
              rows={7}
              value={paste}
              placeholder={
                'Priya Sharma, priya@school.in, Teacher, cbse-grade-10_a, Mathematics; Science, Yes\n' +
                'Priya Sharma, priya@school.in, Teacher, cbse-grade-10_b, Mathematics, No\n' +
                'Rohan Mehta, rohan@school.in, Admin'
              }
              onChange={(e) => setPaste(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: fontPx(13),
                color: 'var(--text-body)',
                background: 'var(--surface-card)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--control-radius)',
                outline: 'none',
                resize: 'vertical',
              }}
            />
          )}
        </Field>

        {rooms.length > 0 && (
          <div className="rounded-lg border border-line px-3 py-2.5">
            <p className="mb-1.5 text-xs font-semibold text-ink">Class codes</p>
            <div className="flex flex-wrap gap-1.5">
              {rooms.map((room) => (
                <code
                  key={room.id}
                  className="rounded bg-paper-dim px-1.5 py-0.5 text-fs-11.5 font-semibold text-forest"
                  title={room.name}
                >
                  {room.code}
                </code>
              ))}
            </div>
            {firstRoom && subjectNames.length > 0 && (
              <>
                <p className="mb-1.5 mt-2.5 text-xs font-semibold text-ink">
                  Subjects in {firstRoom.name}
                </p>
                <p className="text-xs text-ink-soft">{subjectNames.join(' · ')}</p>
              </>
            )}
          </div>
        )}

        {parsed.errors.length > 0 && (
          <div role="alert" className="text-sm text-danger">
            <p>Fix these rows before importing. No rows have been submitted.</p>
            <ul className="mt-1 list-disc pl-5">
              {parsed.errors.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          </div>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex items-center gap-3">
          <Button
            disabled={people === 0 || parsed.errors.length > 0 || importStaff.isPending}
            onClick={() => {
              setError('');
              importStaff.mutate();
            }}
          >
            {importStaff.isPending
              ? 'Importing…'
              : `Import ${people} staff member${people === 1 ? '' : 's'}`}
          </Button>
          {paste.trim() && parsed.staff.length === 0 && parsed.errors.length === 0 && (
            <span className="text-sm text-danger">
              Could not read any rows. Each line needs at least a name and an email.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

/** What an import did: who was added (with any new password, once) and who was not. */
export function StaffImportResult({
  result,
  onDone,
}: {
  result: StaffImportResultDTO;
  onDone: () => void;
}) {
  const withPassword = result.created.filter((row) => row.temporaryPassword);
  return (
    <Card
      title={`Added ${result.created.length} staff member${result.created.length === 1 ? '' : 's'}`}
    >
      {result.failed.length > 0 && (
        <div className="mb-3">
          <p className="text-sm font-semibold text-danger">
            {result.failed.length} {result.failed.length === 1 ? 'person' : 'people'} could not be
            added
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {result.failed.map((row) => (
              <li key={row.email} className="text-xs text-ink-soft">
                {row.rows.length === 1 ? 'Row' : 'Rows'} {row.rows.join(', ')} ({row.email}) —{' '}
                {row.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.created.length > 0 && (
        <>
          <p className="mb-2 text-sm text-ink-soft">
            New staff sign in at <strong>/staff</strong> with their email and the temporary password
            below, and must change it first. These passwords are shown only now — save or print them
            before you close this. Anyone who already had a CWY account keeps their own password.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                  <th className="py-1.5 text-left font-semibold">Name</th>
                  <th className="py-1.5 text-left font-semibold">Email</th>
                  <th className="py-1.5 text-left font-semibold">Role</th>
                  <th className="py-1.5 text-left font-semibold">Classes</th>
                  <th className="py-1.5 text-left font-semibold">Temporary password</th>
                </tr>
              </thead>
              <tbody>
                {result.created.map((row) => (
                  <tr key={row.email} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td className="py-1.5">{row.name}</td>
                    <td className="py-1.5">{row.email}</td>
                    <td className="py-1.5">{MEMBERSHIP_ROLE_LABEL[row.role]}</td>
                    <td className="py-1.5 text-ink-soft">
                      {row.scopeCount > 0
                        ? `${row.scopeCount} subject${row.scopeCount === 1 ? '' : 's'}`
                        : '—'}
                      {row.classTeacherOf ? ` · class teacher of ${row.classTeacherOf}` : ''}
                    </td>
                    <td className="py-1.5 font-semibold" data-numeric="">
                      {row.temporaryPassword ?? 'Existing account'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {withPassword.length > 0 && (
          <Button
            variant="secondary"
            onClick={() =>
              downloadCsv('staff-sign-in-details.csv', [
                ['Name', 'Email', 'Temporary password'],
                ...withPassword.map((row) => [row.name, row.email, row.temporaryPassword!]),
              ])
            }
          >
            Download sign-in details
          </Button>
        )}
        <Button variant="secondary" onClick={() => window.print()}>
          Print
        </Button>
        <Button
          onClick={() => {
            if (
              withPassword.length === 0 ||
              window.confirm('Close this? The temporary passwords cannot be shown again.')
            ) {
              onDone();
            }
          }}
        >
          Done
        </Button>
      </div>
    </Card>
  );
}
